import { type KeyboardEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import RemoteDialog, { useRemoteDialogOpener } from '@/features/app/dialogs/RemoteDialog';
import { navigateParentToAssetDetail } from '@/features/app/MessageHandler';
import { STATUS } from '@/features/engineoil/utils/constants';
import { SHOW_WORKSHOP_FILTER_PARAM } from './dialogs/TableSettingsDialog';
import EngineOilValueCell from './EngineOilValueCell';
import Pagination, { PAGE_SIZE_OPTIONS } from './Pagination';
import SortingHeader from './SortingHeader';
import TableCell from './TableCell';
import { ENGINE_OIL_TABLE_SORT_KEY, type EngineOilRow, type EngineOilSortBy, type SortOrder } from './types';
import { useColumnSettings } from './useColumnSettings';
import { useCsvExport } from './useCsvExport';

export const getRowClassName = (row: EngineOilRow): string => {
    if (row.status === STATUS.DANGER) {
        return 'danger';
    }
    if (row.status === STATUS.WARNING) {
        return 'warning';
    }
    return '';
};

const VehicleNameLink = ({ assetId, name }: { assetId: string; name: string }) => {
    const openDetail = () => navigateParentToAssetDetail(assetId);
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDetail();
        }
    };

    return (
        <span
            className="cursor-pointer hover-text-bold text-nowrap"
            onClick={openDetail}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <span
                className="uikit-icon icon-angle-double-right
            text-color-danger text-size-14 margin-top-0 width-15"
            />
            &nbsp;{name}
        </span>
    );
};

interface EngineOilTableProps {
    rows: EngineOilRow[];
    inProgress?: boolean;
    sortBy: EngineOilSortBy;
    sortOrder: SortOrder;
    onSort: (key: EngineOilSortBy) => void;
    page: number;
    pageSize: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    assetIds: string[];
    accountIds: string[];
    workshopIds: string[];
}

const CSV_HEADERS: { key: string; messageId: string }[] = [
    { key: 'VEHICLE_NAME', messageId: 'intl-msg:engineOil.table.vehicleName' },
    { key: 'VIN', messageId: 'intl-msg:engineOil.table.vin' },
    { key: 'CUSTOMER', messageId: 'intl-msg:engineOil.table.customer' },
    { key: 'ADDRESS', messageId: 'intl-msg:engineOil.table.customerDetails.address' },
    { key: 'WORKSHOP', messageId: 'intl-msg:engineOil.table.workshop' },
    { key: 'WORKSHOP_CITY', messageId: 'intl-msg:engineOil.table.workshopCity' },
    { key: 'VALUE', messageId: 'intl-msg:engineOil.table.value' },
    { key: 'DATA_TIMESTAMP', messageId: 'intl-msg:engineOil.table.dataTimestamp' },
    { key: 'MILEAGE', messageId: 'intl-msg:engineOil.table.mileage' },
];

const EngineOilTable = ({
    rows,
    inProgress = false,
    sortBy,
    sortOrder,
    onSort,
    page,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    assetIds,
    accountIds,
    workshopIds,
}: EngineOilTableProps) => {
    const openRemoteDialog = useRemoteDialogOpener();
    const columnSettings = useColumnSettings();
    const intl = useIntl();
    const { exportCsv, isExporting } = useCsvExport();

    const handleCsvExport = () => {
        exportCsv({
            ...(assetIds.length > 0 && { asset_ids: assetIds }),
            ...(accountIds.length > 0 && { account_ids: accountIds }),
            ...(workshopIds.length > 0 && { workshop_ids: workshopIds }),
            csv_headers: CSV_HEADERS.map(({ key, messageId }) => ({
                header_key: key,
                header_value: intl.formatMessage({ id: messageId }),
            })),
        });
    };

    // Vehicle is always shown; only the optional columns are configurable.
    const showVehicle = true;
    const showVin = columnSettings.vin;
    const showCustomer = columnSettings.customer;
    // Workshop is only present when NSC user
    const isNsc = rows.some((row) => row.workshop.trim() !== '');
    const showWorkshop = columnSettings.workshop && isNsc;

    const hasRows = rows.length > 0;
    const columnCount = [showVehicle, showVin, showWorkshop, showCustomer].filter(Boolean).length + 1;

    const openSettingsDialog = () =>
        openRemoteDialog('/table/settings', 'sm', isNsc ? { [SHOW_WORKSHOP_FILTER_PARAM]: 'true' } : {});

    const renderHeaderCell = (sortKey: EngineOilSortBy, messageId: string, hidden = false) => (
        <SortingHeader
            isSorted={sortBy === sortKey}
            sortOrder={sortOrder}
            onClick={() => onSort(sortKey)}
            hidden={hidden}
        >
            <FormattedMessage id={messageId} />
        </SortingHeader>
    );

    return (
        <div>
            <div className="display-flex justify-content-end align-items-start margin-bottom-20">
                <OverlayTrigger
                    placement="bottom-start"
                    overlay={
                        <Tooltip>
                            <FormattedMessage id="intl-msg:engineOil.table.settings.title" />
                        </Tooltip>
                    }
                >
                    <Button iconOnly iconName="settings" onClick={openSettingsDialog} />
                </OverlayTrigger>
                <Button
                    className="margin-left-10"
                    onClick={handleCsvExport}
                    disabled={isExporting || !rows.length}
                    data-testid="csv-export-button"
                >
                    <FormattedMessage id="intl-msg:engineOil.table.exportCsv" />
                </Button>
            </div>

            <div className="table-responsive">
                <table className="EngineOilTable table table-bordered table-hover" data-testid="engine-oil-table">
                    <thead>
                        <tr>
                            {renderHeaderCell(
                                ENGINE_OIL_TABLE_SORT_KEY.VEHICLE_NAME,
                                'intl-msg:engineOil.table.vehicleName',
                                !showVehicle,
                            )}
                            {renderHeaderCell(ENGINE_OIL_TABLE_SORT_KEY.VIN, 'intl-msg:engineOil.table.vin', !showVin)}
                            {renderHeaderCell(
                                ENGINE_OIL_TABLE_SORT_KEY.WORKSHOP,
                                'intl-msg:engineOil.table.workshop',
                                !showWorkshop,
                            )}
                            {renderHeaderCell(
                                ENGINE_OIL_TABLE_SORT_KEY.CUSTOMER,
                                'intl-msg:engineOil.table.customer',
                                !showCustomer,
                            )}
                            {renderHeaderCell(ENGINE_OIL_TABLE_SORT_KEY.VALUE, 'intl-msg:engineOil.table.value')}
                        </tr>
                    </thead>
                    <tbody data-testid="table-body">
                        {inProgress || !hasRows ? (
                            <tr>
                                <td colSpan={columnCount} className="text-center padding-25 text-color-dark">
                                    {inProgress ? (
                                        <Spinner
                                            text={<FormattedMessage id="intl-msg:engineOil.table.loading" />}
                                            isInverse={false}
                                        />
                                    ) : (
                                        <NotFoundState
                                            outerClassName="border-none"
                                            headline={<FormattedMessage id="intl-msg:engineOil.table.empty" />}
                                            message={<FormattedMessage id="intl-msg:engineOil.table.emptyHint" />}
                                        />
                                    )}
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.id} data-id={row.id} className={getRowClassName(row) || undefined}>
                                    <TableCell hidden={!showVehicle}>
                                        <VehicleNameLink assetId={row.assetId} name={row.vehicleName} />
                                    </TableCell>
                                    <TableCell hidden={!showVin}>{row.vin}</TableCell>
                                    <TableCell hidden={!showWorkshop}>
                                        {row.workshop ? (
                                            <RemoteDialog path={`/table/${row.workshopId}/workshop`}>
                                                {row.workshop}
                                            </RemoteDialog>
                                        ) : (
                                            row.workshop
                                        )}
                                    </TableCell>
                                    <TableCell hidden={!showCustomer}>
                                        {row.customer ? (
                                            <RemoteDialog path={`/table/${row.assetId}/assignment`}>
                                                {row.customer}
                                            </RemoteDialog>
                                        ) : (
                                            row.customer
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <EngineOilValueCell row={row} />
                                    </TableCell>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {hasRows && (
                <Pagination
                    pageCount={totalPages}
                    selectedPage={page}
                    onPageSelect={onPageChange}
                    pageOptions={PAGE_SIZE_OPTIONS.map((size) => ({
                        value: size,
                        isSelected: size === pageSize,
                        onSelect: () => onPageSizeChange(size),
                    }))}
                />
            )}
        </div>
    );
};

export default EngineOilTable;
